// src/DoctorProfile.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";

import { db, auth, storage } from "./firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import DoctorProfileSeo from "./DoctorProfileSeo";
import { buildDoctorSeoData } from "./doctorProfileSeoData";
import { styles, globalStyles } from "./style/DoctorProfile.styles";

/** Children (split components) */
import ProfileHeaderCard, { TinyPageHeader } from "./components/doctor-profile/ProfileHeaderCard";
import ReviewsSection from "./components/doctor-profile/ReviewsSection";
import AboutSection from "./components/doctor-profile/AboutSection";
import PracticeLocation from "./components/doctor-profile/PracticeLocation";
import ServicesAndBooking from "./components/doctor-profile/ServicesAndBooking";
import ResearchGrid from "./components/doctor-profile/ResearchGrid";
import ProfessionalDetails from "./components/doctor-profile/ProfessionalDetails";
import WeeklySchedule from "./components/doctor-profile/WeeklySchedule";
import BookingModalBridge from "./components/doctor-profile/BookingModalBridge";

/** Icons only for error/loading states */
import { Activity, ArrowLeft } from "lucide-react";

/* =========================================================
   GOOGLE MAPS LOADER
   ========================================================= */
const ensureGoogleMaps = (() => {
  let p;
  return (apiKey) => {
    if (window.google?.maps?.Map) return Promise.resolve(window.google.maps);
    if (p) return p;
    p = new Promise((resolve, reject) => {
      if (!apiKey) return reject(new Error("No Google Maps API key present"));
      window.gm_authFailure = () => {
        console.error("Google Maps auth failed (key/referrer/billing).");
        reject(new Error("gm_authFailure"));
      };
      const s = document.createElement("script");
      s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=weekly&libraries=places`;
      s.async = true;
      s.defer = true;
      s.onload = () => {
        if (!window.google?.maps?.Map) {
          reject(new Error("Maps script loaded but Map ctor missing"));
          return;
        }
        resolve(window.google.maps);
      };
      s.onerror = () => reject(new Error("Network error loading Maps JS"));
      document.head.appendChild(s);
    });
    return p;
  };
})();

function coerceLatLng(coords) {
  if (!coords) return null;
  const lat = Number(coords.lat);
  const lng = Number(coords.lng);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  return null;
}

function onVisible(el, cb) {
  if (!el) return () => {};
  const box = el.getBoundingClientRect();
  if (box.height > 0 && box.width > 0) {
    cb();
    return () => {};
  }
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting && e.target.getBoundingClientRect().height > 0) {
          cb();
          io.disconnect();
          break;
        }
      }
    },
    { root: null, threshold: 0.01 }
  );
  io.observe(el);
  return () => io.disconnect();
}

/* ========================================================= */

const REGISTER_ROUTE = "/auth?mode=register&role=patient"; // ⬅️ set to your real route

const DoctorProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Affiliate (optional)
  const affiliate = searchParams.get("aff") || searchParams.get("utm_source") || "";
  useEffect(() => {
    if (affiliate) sessionStorage.setItem("md_aff", affiliate);
  }, [affiliate]);

  // core state
  const [doctor, setDoctor] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  // ui state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // photo upload
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // booking
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  // role state
  const [userRole, setUserRole] = useState(null); // "patient" | "doctor" | "clinic" | "admin" | null
  const [roleResolved, setRoleResolved] = useState(false);

  // register prompt state (guest/null-role)
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);

  // reviews
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);

  // maps
  const [mapLoaded, setMapLoaded] = useState(false);
  const [googleMapsReady, setGoogleMapsReady] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  const isOwnProfile = !!currentUser && currentUser.uid === id;
  const isGuest = !currentUser;
  const canBook = roleResolved && !!currentUser && userRole === "patient";
  const isRoleUnset = roleResolved && !!currentUser && userRole == null;

  const buildNextUrl = () =>
    typeof window !== "undefined"
      ? window.location.pathname + window.location.search
      : "/";

  const goRegisterAsPatient = () => {
    const next = encodeURIComponent(buildNextUrl());
    const affParam = affiliate ? `&aff=${encodeURIComponent(affiliate)}` : "";
    navigate(`${REGISTER_ROUTE}&next=${next}${affParam}`);
  };

  /** ===== Auth bootstrap ===== */
  useEffect(() => {
    setCurrentUser(auth.currentUser);
  }, []);

  /** ===== Resolve role (custom claim → Firestore fallback) ===== */
  useEffect(() => {
    let cancelled = false;
    const resolveRole = async () => {
      if (!currentUser) {
        if (!cancelled) {
          setUserRole(null);
          setRoleResolved(true);
        }
        return;
      }
      try {
        const tokenResult = await currentUser.getIdTokenResult();
        const claimRole = tokenResult?.claims?.role;
        if (!cancelled && claimRole) {
          setUserRole(String(claimRole));
          setRoleResolved(true);
          return;
        }
      } catch {/* noop */}

      try {
        const uref = doc(db, "users", currentUser.uid);
        const usnap = await getDoc(uref);
        const role = usnap.exists() ? usnap.data()?.role ?? null : null;
        if (!cancelled) {
          setUserRole(role ? String(role) : null);
          setRoleResolved(true);
        }
      } catch (e) {
        console.error("Failed to fetch user role from Firestore:", e);
        if (!cancelled) {
          setUserRole(null);
          setRoleResolved(true);
        }
      }
    };

    resolveRole();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  /** ===== Reviews helpers ===== */
  const fetchDoctorReviews = async (doctorId) => {
    try {
      setReviewsLoading(true);
      const reviewsQuery = query(
        collection(db, "reviews"),
        where("doctorId", "==", doctorId),
        where("status", "==", "published")
      );
      const reviewsSnapshot = await getDocs(reviewsQuery);
      const reviewsData = reviewsSnapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate() || new Date(),
      }));
      reviewsData.sort((a, b) => b.createdAt - a.createdAt);
      setReviews(reviewsData);
    } catch (e) {
      console.error("Error fetching reviews:", e);
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  };

  const reviewStats = useMemo(() => {
    if (!reviews || reviews.length === 0) {
      return {
        averageRating: 0,
        totalReviews: 0,
        ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
        categoryAverages: {
          communication: 0,
          punctuality: 0,
          professionalism: 0,
          expertise: 0,
        },
        recommendationPercentage: 0,
      };
    }

    const totalRating = reviews.reduce((sum, r) => sum + (r.rating || 0), 0);
    const averageRating = totalRating / reviews.length;

    const ratingDistribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach((r) => {
      if (ratingDistribution[r.rating] != null) ratingDistribution[r.rating]++;
    });

    const categoryAverages = {
      communication: 0,
      punctuality: 0,
      professionalism: 0,
      expertise: 0,
    };
    Object.keys(categoryAverages).forEach((category) => {
      const categoryTotal = reviews.reduce(
        (sum, r) => sum + (r.categories?.[category] || 0),
        0
      );
      categoryAverages[category] = reviews.length > 0 ? categoryTotal / reviews.length : 0;
    });

    const recommendations = reviews.filter((r) => r.wouldRecommend).length;
    const recommendationPercentage = (recommendations / reviews.length) * 100;

    return {
      averageRating,
      totalReviews: reviews.length,
      ratingDistribution,
      categoryAverages,
      recommendationPercentage,
    };
  }, [reviews]);

  /** ===== Maps: init + fallback ===== */
  const initializeFallbackMap = () => {
    if (!mapRef.current || !doctor?.location?.coordinates) return;
    const container = mapRef.current;
    container.innerHTML = "";

    const mapDiv = document.createElement("div");
    mapDiv.style.cssText = `
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 50%, #90caf9 100%);
      position: relative;
      overflow: hidden;
      border-radius: 12px;
    `;

    const gridCanvas = document.createElement("canvas");
    gridCanvas.width = 300;
    gridCanvas.height = 300;
    gridCanvas.style.cssText = `
      position: absolute; top:0; left:0; width:100%; height:100%;
      opacity: 0.3; pointer-events:none;
    `;
    const ctx = gridCanvas.getContext("2d");
    ctx.strokeStyle = "#1976d2";
    ctx.lineWidth = 0.5;
    for (let i = 0; i <= 300; i += 20) {
      ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i, 300); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, i); ctx.lineTo(300, i); ctx.stroke();
    }

    const marker = document.createElement("div");
    marker.style.cssText = `
      position: absolute; width: 20px; height: 20px;
      background: #2563EB; border: 3px solid white; border-radius: 50%;
      box-shadow: 0 4px 8px rgba(0,0,0,0.3);
      transform: translate(-50%, -50%); z-index: 10; left: 50%; top: 50%;
    `;

    const infoPanel = document.createElement("div");
    infoPanel.style.cssText = `
      position: absolute; bottom:12px; left:12px; right:12px;
      background: rgba(255,255,255,0.95); padding: 12px; border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15); backdrop-filter: blur(4px);
      font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    `;
    infoPanel.innerHTML = `
      <div style="font-weight: 600; color: #2563EB; margin-bottom: 4px;">${doctor?.name || ""}</div>
      <div style="font-size: 12px; color: #666; margin-bottom: 4px;">${doctor?.specialty || ""}</div>
      <div style="font-size: 11px; color: #888;">${doctor?.location?.address || ""}</div>
    `;

    mapDiv.appendChild(gridCanvas);
    mapDiv.appendChild(marker);
    mapDiv.appendChild(infoPanel);
    container.appendChild(mapDiv);
  };

  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    const container = mapRef.current;
    const coords = coerceLatLng(doctor?.location?.coordinates);

    if (!mapLoaded) setMapLoaded(true);
    if (!container) return;

    if (!coords) {
      setGoogleMapsReady(false);
      return;
    }
    if (!apiKey) {
      setGoogleMapsReady(false);
      initializeFallbackMap();
      return;
    }

    const init = async () => {
      try {
        const gmaps = await ensureGoogleMaps(apiKey);
        setGoogleMapsReady(true);
        try { await gmaps.importLibrary?.("maps"); } catch {/* noop */}
        // (Initialize interactive map here if needed)
      } catch (err) {
        console.error("Maps init failed:", err?.message || err);
        setGoogleMapsReady(false);
        initializeFallbackMap();
      }
    };

    const stopIO = onVisible(container, init);
    return () => stopIO?.();
  }, [doctor?.name, doctor?.specialty, doctor?.location?.coordinates, mapLoaded]);

  const directionsUrl = useMemo(() => {
    const c = coerceLatLng(doctor?.location?.coordinates);
    if (!c) return "#";
    return `https://www.google.com/maps/dir/?api=1&destination=${c.lat},${c.lng}`;
  }, [doctor?.location?.coordinates]);

  /** ===== Upload photo ===== */
  const onUploadPhoto = async (file) => {
    if (!file || !isOwnProfile) return;
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert("File size must be less than 5MB");
      return;
    }
    setUploadingPhoto(true);
    try {
      const storageRef = ref(storage, `doctor_photos/${id}/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      const photoURL = await getDownloadURL(snapshot.ref);
      const doctorRef = doc(db, "doctors", id);
      await updateDoc(doctorRef, { photoURL });
      setDoctor((prev) => ({ ...prev, photoURL }));
      alert("Profile photo updated successfully!");
    } catch (error) {
      console.error("Error uploading photo:", error);
      alert("Failed to upload photo. Please try again.");
    } finally {
      setUploadingPhoto(false);
    }
  };

  /** ===== SEO derivations ===== */
  const href = (typeof window !== "undefined" && window.location?.href) || "";
  const origin = (typeof window !== "undefined" && window.location?.origin) || "";
const seo = buildDoctorSeoData(doctor, { baseUrl: "https://www.mikdok.com" });
  const canonicalUrl = href;
  const pageTitle = doctor?.name
    ? `${doctor.name} — ${doctor.specialty || "Physician"}`
    : "Doctor Profile";
  const pageDesc =
    doctor?.bio?.slice?.(0, 160) ||
    `Learn more about ${doctor?.name || "this doctor"}, their specialty${
      doctor?.specialty ? ` in ${doctor?.specialty}` : ""
    }, location, services, and reviews.`;

  const toOpeningHoursSpecification = (workingHours) => {
    if (!workingHours || typeof workingHours !== "object") return [];
    const map = {
      monday: "Monday",
      tuesday: "Tuesday",
      wednesday: "Wednesday",
      thursday: "Thursday",
      friday: "Friday",
      saturday: "Saturday",
      sunday: "Sunday",
    };
    const specs = [];
    Object.keys(map).forEach((k) => {
      const v = workingHours[k];
      if (v?.enabled && v?.start && v?.end) {
        specs.push({
          "@type": "OpeningHoursSpecification",
          dayOfWeek: map[k],
          opens: v.start,
          closes: v.end,
        });
      }
    });
    return specs;
  };

  const openingHoursSpecification = toOpeningHoursSpecification(doctor?.workingHours);

  const aggregateRating =
    reviewStats?.totalReviews > 0
      ? {
          "@type": "AggregateRating",
          ratingValue: Number(reviewStats.averageRating.toFixed(1)),
          reviewCount: reviewStats.totalReviews,
          bestRating: 5,
          worstRating: 1,
        }
      : undefined;

  const address = doctor?.location?.address
    ? { "@type": "PostalAddress", streetAddress: doctor.location.address }
    : undefined;

  const geo = doctor?.location?.coordinates
    ? { "@type": "GeoCoordinates", latitude: doctor.location.coordinates.lat, longitude: doctor.location.coordinates.lng }
    : undefined;

  const sameAs = Array.isArray(doctor?.socials) ? doctor.socials.filter(Boolean) : [];

  const bookingTarget = canonicalUrl;
  const potentialAction = { "@type": "ReserveAction", target: bookingTarget, name: "Book an appointment" };

  const doctorJsonLd = {
    "@context": "https://schema.org",
    "@type": "Physician",
    name: doctor?.name || "Doctor",
    ...(doctor?.specialty ? { medicalSpecialty: doctor.specialty } : {}),
    ...(doctor?.photoURL ? { image: doctor.photoURL } : {}),
    ...(address ? { address } : {}),
    ...(geo ? { geo } : {}),
    ...(doctor?.phone ? { telephone: doctor.phone } : {}),
    ...(openingHoursSpecification.length ? { openingHoursSpecification } : {}),
    ...(aggregateRating ? { aggregateRating } : {}),
    ...(sameAs.length ? { sameAs } : {}),
    ...(bookingTarget ? { potentialAction } : {}),
    url: canonicalUrl,
  };

  const breadcrumbs = [
    { name: "Home", item: `${origin}/` },
    { name: "Doctors", item: `${origin}/doctors` },
    { name: doctor?.name || "Profile", item: canonicalUrl },
  ];

  /** ===== Load doctor + uploads ===== */
  useEffect(() => {
    const fetchDoctorAndUploads = async () => {
      try {
        const docRef = doc(db, "doctors", id);
        const snapshot = await getDoc(docRef);
        if (!snapshot.exists()) {
          setError("Doctor not found");
          return;
        }
        const doctorData = snapshot.data();
        setDoctor(doctorData);

        try {
          const uploadsQuery = query(
            collection(db, "uploads", id, "items"),
            orderBy("createdAt", "desc")
          );
          const uploadsSnapshot = await getDocs(uploadsQuery);
          const uploadsData = uploadsSnapshot.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            createdAt: d.data().createdAt?.toDate() || new Date(),
          }));

          await fetchDoctorReviews(id);
          setUploads(uploadsData);
        } catch (uploadsError) {
          console.log("No uploads found or error fetching uploads:", uploadsError);
          setUploads([]);
        }
      } catch (err) {
        console.error("Error fetching doctor:", err);
        setError("Failed to load doctor profile");
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchDoctorAndUploads();
  }, [id]);

  /** ===== Patient-only booking gate ===== */
  const requirePatientToBook = (svc = null) => {
    // Guests and null-role users see the choice modal (Register vs Continue)
    if (!currentUser || (roleResolved && userRole == null)) {
      setShowRegisterPrompt(true);
      return;
    }
    if (!canBook) {
      alert("Only patient accounts can book appointments.");
      return;
    }
    setSelectedService(svc);
    setShowBookingModal(true);
  };

  /** ===== Render ===== */
  if (loading) {
    return (
      <div style={styles.container}>
        <style>{globalStyles}</style>
       
       
        <div style={styles.loadingContainer}>
          <div style={styles.loadingSpinner}></div>
          <p style={{ color: "#6b7280", fontWeight: 500 }}>Loading doctor profile...</p>
        </div>
      </div>
    );
  }

  if (error || !doctor) {
    return (
      <div style={styles.container}>
        <style>{globalStyles}</style>
       <DoctorProfileSeo
  title={seo.title}
  description={seo.description}
  canonicalUrl={seo.canonicalUrl}
  imageUrl={doctor?.photoURL}
  doctorJsonLd={seo.doctorJsonLd}
  breadcrumbs={seo.breadcrumbs}
  keywords={seo.keywords}
/>
        <div style={styles.errorContainer}>
          <div style={styles.errorCard} role="alert">
            <Activity style={{ width: 48, height: 48, color: "#ef4444", margin: "0 auto 16px auto" }} />
            <h2 style={{ fontSize: 24, fontWeight: 700, color: "#111827", marginBottom: 8 }}>
              {error || "Doctor Not Found"}
            </h2>
            <p style={{ color: "#6b7280", marginBottom: 24 }}>
              The doctor profile you're looking for doesn't exist or has been removed.
            </p>
            <button
              style={styles.primaryButton}
              className="primary-button focus-ring"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft style={{ width: 16, height: 16 }} />
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.fullBleed}>
      <style>{globalStyles}</style>

      {/* SEO */}
      <DoctorProfileSeo
        title={pageTitle}
        description={pageDesc}
        canonicalUrl={canonicalUrl}
        imageUrl={doctor?.photoURL}
        doctorJsonLd={doctorJsonLd}
        breadcrumbs={breadcrumbs}
      />

      {/* Tiny header */}
      <div style={styles.pageWrap}>
        <div style={styles.contentColumn}>
          <TinyPageHeader doctorName={doctor?.name} />
        </div>
      </div>

      {/* Header card */}
      <div style={styles.pageWrap}>
        <div style={styles.contentColumn} className="main-content">
          <ProfileHeaderCard
            doctor={doctor}
            reviewStats={reviewStats}
            experienceYears={doctor?.startedPracticeYear ? new Date().getFullYear() - doctor.startedPracticeYear : null}
            isOwnProfile={isOwnProfile}
            uploadingPhoto={uploadingPhoto}
            onUploadPhoto={onUploadPhoto}
            onBook={() => requirePatientToBook(null)}
            onMessage={() => {}}
          />

          {/* 2-column content grid */}
          <div style={styles.contentGrid} className="content-grid" aria-live="polite">
            {/* Left */}
            <div>
              {/* Guest / Null-role banner CTA */}
              {(!currentUser || isRoleUnset) && (
                <div
                  style={{
                    margin: "12px 0 16px",
                    padding: 16,
                    background: "#EFF6FF",
                    border: "1px solid #BFDBFE",
                    borderRadius: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ color: "#1E40AF", fontSize: 14, lineHeight: 1.5 }}>
                    <strong>Create a free MikDok account</strong> to book this doctor and manage your
                    medical records.
                    {affiliate && <span> (Invited via <b>{affiliate}</b>)</span>}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => setShowRegisterPrompt(true)}
                      style={{
                        padding: "8px 14px",
                        borderRadius: 8,
                        border: "none",
                        fontWeight: 700,
                        color: "white",
                        background: "linear-gradient(135deg, #3B82F6, #6366F1)",
                        cursor: "pointer",
                      }}
                    >
                      Register as Patient
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowRegisterPrompt(false)}
                      style={{
                        padding: "8px 14px",
                        borderRadius: 8,
                        border: "1px solid #93C5FD",
                        fontWeight: 600,
                        color: "#1E40AF",
                        background: "white",
                        cursor: "pointer",
                      }}
                    >
                      Continue as Guest
                    </button>
                  </div>
                </div>
              )}

              <ReviewsSection
                reviews={reviews}
                reviewsLoading={reviewsLoading}
                reviewStats={reviewStats}
              />

              <AboutSection doctor={doctor} />

              <PracticeLocation
                doctorName={doctor?.name}
                location={doctor?.location}
                mapLoaded={mapLoaded}
                googleMapsReady={googleMapsReady}
                onFallbackInit={initializeFallbackMap}
                mapRef={mapRef}
                directionsUrl={directionsUrl}
                mapHeight={360}
              />

              {currentUser && !canBook && (
                <div
                  style={{
                    margin: "8px 0 16px",
                    padding: "10px 12px",
                    background: "#FFF7ED",
                    border: "1px solid #FED7AA",
                    borderRadius: 8,
                    color: "#9A3412",
                    fontSize: 13,
                  }}
                >
                  You’re signed in as <b>{userRole || "unknown"}</b>. Only patient accounts can book
                  appointments.
                </div>
              )}

              <ServicesAndBooking
                services={doctor?.services || []}
                canBook={canBook && !!currentUser}
                showRegisterPatient={!currentUser || isRoleUnset}
                onRegisterPatient={() => setShowRegisterPrompt(true)}
                onBook={(svc) => requirePatientToBook(svc)}
              />

              <ResearchGrid doctorId={id} uploads={uploads} />
            </div>

            {/* Right */}
            <div>
              <ProfessionalDetails doctor={doctor} />
              {doctor?.workingHours && <WeeklySchedule workingHours={doctor.workingHours} />}
            </div>
          </div>
        </div>
      </div>

      {/* Booking modal (patients only) */}
      {canBook && (
        <BookingModalBridge
          open={showBookingModal}
          doctor={doctor}
          selectedService={selectedService}
          onClose={() => { setShowBookingModal(false); setSelectedService(null); }}
          onSuccess={() => { setShowBookingModal(false); setSelectedService(null); }}
          afterSuccessNavigateTo="/dashboard-patient"
        />
      )}

      {/* Small Register Prompt (choice modal) */}
      {showRegisterPrompt && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setShowRegisterPrompt(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "grid",
            placeItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(520px, 94vw)",
              background: "white",
              borderRadius: 12,
              padding: 20,
              boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
            }}
          >
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#111827" }}>
              Book faster with a free patient account
            </h3>
            <p style={{ marginTop: 8, color: "#374151", fontSize: 14, lineHeight: 1.6 }}>
              Create an account to save your info, manage appointments, and keep your medical records in one place.
            </p>

            <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => setShowRegisterPrompt(false)}
                style={{
                  padding: "10px 14px",
                  borderRadius: 8,
                  border: "1px solid #E5E7EB",
                  background: "white",
                  color: "#111827",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Continue as Guest
              </button>
              <button
                type="button"
                onClick={goRegisterAsPatient}
                style={{
                  padding: "10px 14px",
                  borderRadius: 8,
                  border: "none",
                  background: "linear-gradient(135deg, #10B981, #059669)",
                  color: "white",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Register as Patient
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorProfile;
